/**
 * The app's stacking scale — one place, so "what sits above what" is a decision
 * rather than an accident of whoever typed the biggest number last.
 *
 * WHY THIS EXISTS (#627, and with it #648's SC 2.4.11 and #642's second symptom):
 * the floating item-card layer painted at `zIndex: 2000 + z`, above MUI's modal
 * layer (`theme.zIndex.modal = 1300`). Consequences, all one root cause:
 *
 *   - #627  a dialog opened while a card is on screen is VISIBLE and ENABLED but
 *           cannot be clicked — the card's subtree intercepts the pointer event.
 *   - #648  SC 2.4.11 "Focus Not Obscured" (WCAG 2.2 AA): a focused control behind
 *           the card region has its focus ring completely hidden. That is an
 *           accessibility failure, not merely a click bug.
 *   - #642  the `zz_verify_notifications` pointer-interception timeout.
 *
 * And it was getting WORSE with use, not staying still. `ItemCardContext`'s `zRef`
 * is a monotonically incrementing counter — every open/focus bumps it forever — so
 * `2000 + z` climbs without bound. `ConflictPromptHost` had already patched around
 * the symptom with a hardcoded `zIndex: 3000`; that workaround was a time bomb,
 * because enough card focus events would have sailed past 3000 too.
 *
 * THE RULE: floating item cards are a WORKSPACE surface — persistent, non-modal,
 * something you keep beside your work. A dialog is a MODAL interruption that must
 * always win. So cards sit BELOW `modal`, above the drawers and the shell.
 *
 * Everything here is below MUI's 1300 on purpose. If you need something above a
 * dialog, it is almost certainly a dialog itself — use MUI's own layer, don't add
 * a bigger number here.
 */

/** MUI defaults, restated so the ceiling is visible at the point of use. */
export const Z_MUI_DRAWER = 1200;
export const Z_MUI_MODAL = 1300;

/** App surfaces that sit under the cards (see `styles/screens.css`). */
export const Z_DRAWER_SCRIM = 1198;
export const Z_LINK_DRAWER = 1199;

/**
 * The mobile assistant's floating "Back" button (`RequirementsTreeView`, rendered
 * inside `.aicol.ai-overlay`). `ChatInterface` mounts a `position: fixed` root at
 * `zIndex: 1000` on mobile, so the button has to outrank that to stay tappable —
 * which is the whole reason it carries an explicit z-index at all.
 *
 * It previously carried a hardcoded `zIndex: 2000`, i.e. ABOVE `Z_MUI_MODAL`. That
 * is #627's exact mechanism in a second file: with the assistant open, any dialog
 * (Ctrl/Cmd+K command palette, the conflict prompt, AddDialog) had this button
 * painting over its top-left corner and swallowing pointer events there.
 *
 * MUST STAY BELOW `Z_MUI_MODAL`. A dialog is a modal interruption; a shell button
 * behind it is correct behaviour, not a bug. Deliberately below `Z_ITEM_CARD_BASE`
 * too — a floating card is a workspace surface the user positioned, and shell
 * chrome should not punch through it.
 */
export const Z_MOBILE_ASSISTANT_BACK = 1050;

/** Floating item cards: above the drawers, strictly below any dialog. */
export const Z_ITEM_CARD_BASE = 1210;
/**
 * Hard ceiling. `Z_MUI_MODAL - 1`, so no amount of stacking can reach a dialog.
 *
 * `ItemCardContext.zRef` is a MONOTONIC counter — `useRef(10)`, incremented on
 * every `showMini`/`openWindow`/`focusWindow` and never reset or renumbered
 * (`ItemCardLayer` documents why renumbering in the context is not done: `w.key`
 * is `${id}#${z}`, so renumbering would churn React keys). So the raw `z` a long
 * session produces is unbounded, and passing it here straight would peg every
 * surface at this ceiling within ~90 hover events — stacking would stop meaning
 * anything.
 *
 * `ItemCardLayer` therefore converts raw `z` to a small RANK within the currently
 * open set (mini included) before calling `itemCardZ`. THAT is what keeps the
 * value bounded; this clamp is the last line of defence for a caller that hasn't,
 * not the mechanism.
 */
export const Z_ITEM_CARD_MAX = Z_MUI_MODAL - 1;

/**
 * Stacking position for one floating card.
 * @param {number} z per-surface stacking RANK (1 = oldest of the open set) — NOT
 *   `ItemCardContext`'s raw monotonic `z`; see `Z_ITEM_CARD_MAX`.
 * @returns {number} a z-index guaranteed to be < Z_MUI_MODAL
 */
export function itemCardZ(z = 0) {
  const n = Number.isFinite(z) ? Math.max(0, z) : 0;
  return Math.min(Z_ITEM_CARD_BASE + n, Z_ITEM_CARD_MAX);
}
